import type {
  ApolloQueryResult,
  DocumentNode,
  NormalizedCacheObject,
  OperationVariables,
  QueryHookOptions,
  QueryOptions,
  QueryResult,
  QueryTuple,
} from '@apollo/client';

import merge from 'deepmerge';
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  useLazyQuery,
  useQuery,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GetStaticPropsResult } from 'next';
import { useMemo } from 'react';

import { removeObjectUndefineds } from './utils/removeObjectUndefineds';

export type GetStaticPropsReturnType<P> = {
  props: P;
  revalidate?: number | boolean;
};

let internalApolloClient: ApolloClient<NormalizedCacheObject> | undefined;

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

// Define api endpoints
export const RESEARCH_API_URL = process.env.NEXT_PUBLIC_RESEARCH_API_URL;
export const WP_API_URL =
  process.env.NEXT_PUBLIC_STELLATE_API_URL !== 'undefined'
    ? process.env.NEXT_PUBLIC_STELLATE_API_URL
    : `${process.env.NEXT_PUBLIC_WORDPRESS_API_URL}/wp/graphql`;

const ALLOWED_DIRECTIVES = ['wp', 'research'] as const;

type DirectiveName = typeof ALLOWED_DIRECTIVES[number];

const researchAuthLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    ...(typeof window === 'undefined'
      ? { 'x-hasura-admin-secret': process.env.RESEARCH_ADMIN_SECRET }
      : {}),
  },
}));

const wpAuthLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    ...(typeof window === 'undefined'
      ? { Authorization: `Basic ${process.env.WP_ADMIN_SECRET}` }
      : {}),
  },
}));

const apiLinks: Record<DirectiveName, ApolloLink> = {
  wp: wpAuthLink.concat(
    createHttpLink({ uri: WP_API_URL, credentials: 'include' }),
  ),
  research: researchAuthLink.concat(
    createHttpLink({
      uri: RESEARCH_API_URL,
    }),
  ),
};

export function getApolloMultipleLink(): ApolloLink {
  return ApolloLink.split(
    operation => operation.getContext().clientName === Object.keys(apiLinks)[1],
    apiLinks.research,
    apiLinks.wp,
  );
}

export function initializeApolloCache(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  initialState = null,
): ApolloClient<NormalizedCacheObject> {
  // If a page has Next.js data fetching methods that
  // use Apollo Client, the initial state gets hydrated here.
  if (initialState) {
    // Get existing cache, loaded during client side data fetching.
    const existingCache = apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps()/getServerSideProps().
    const data = merge(initialState, existingCache);
    // Restore the cache with the merged data.
    apolloClient.cache.restore(data);
  }

  return apolloClient;
}

export function addApolloState<Params>(
  client: ApolloClient<NormalizedCacheObject>,
  pageProps: GetStaticPropsReturnType<Params>,
): GetStaticPropsReturnType<Params> {
  if (pageProps?.props) {
    pageProps.props = removeObjectUndefineds<Params>(pageProps.props);
    // pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: getApolloMultipleLink(),
    cache: new InMemoryCache(),
  });
}

export function initializeWpApollo(
  initialState: NormalizedCacheObject = null,
): ApolloClient<NormalizedCacheObject> {
  // Only run one instance of the Apollo client.
  let newApolloClient = internalApolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state gets hydrated here
  if (initialState) {
    newApolloClient = initializeApolloCache(newApolloClient, initialState);
  }

  // For SSG and SSR always create a new Apollo Client.
  if (typeof window === 'undefined') {
    return newApolloClient;
  }

  // Create the Apollo Client once in the client.
  if (!internalApolloClient) {
    internalApolloClient = newApolloClient;
  }

  return newApolloClient;
}

export async function queryResearch<T, TVariables = OperationVariables>(
  client: ApolloClient<NormalizedCacheObject>,
  options: QueryOptions<TVariables, T>,
): Promise<ApolloQueryResult<T>> {
  return await queryByClientName<T, TVariables>(client, options, 'research');
}

export async function queryByClientName<T, TVariables = OperationVariables>(
  client: ApolloClient<NormalizedCacheObject>,
  options: QueryOptions<TVariables, T>,
  clientName: DirectiveName,
): Promise<ApolloQueryResult<T>> {
  return await client.query<T, TVariables>({
    ...options,
    context: { clientName },
  });
}

export function useQueryResearch<TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> {
  return useQuery<TData, TVariables>(query, {
    ...options,
    context: { clientName: 'research' },
  });
}

export function useLazyQueryResearch<
  TData = any,
  TVariables = OperationVariables,
>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables>,
): QueryTuple<TData, TVariables> {
  return useLazyQuery<TData, TVariables>(query, {
    ...options,
    context: { clientName: 'research' },
  });
}

export function useApollo(
  pageProps: GetStaticPropsResult<unknown>,
): ApolloClient<NormalizedCacheObject> {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeWpApollo(state), [state]);

  return store;
}
